import { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Divider,
} from "@mui/material";
import { Printer, EllipsisVertical } from "lucide-react";
import ReactiveDialog from "../common/ReactiveDialog";
import DateDisplay from "../common/DateDisplay";
import EmailLetter from "./EmailLetter";
import AttachmentDialog from "../common/AttachmentDialog";
import PopperMenu from "../common/PopperMenu";
import { useGetMemberTaskEmail } from "services/email";
import { EmailMessage } from "protogen/conversation_pb";
import { Attachment as AttachmentProto } from "protogen/common_pb";
import Loading from "../common/Loading";

interface EmailViewDialogProps {
  open: boolean;
  onClose: () => void;
  emailRef: string;
  taskRef: string;
}

export default ({ open, onClose, emailRef, taskRef }: EmailViewDialogProps) => {
  const [email, setEmail] = useState<EmailMessage | null>(null);
  const [openAttachment, setOpenAttachment] = useState<AttachmentProto | null>(
    null,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const { request, loading } = useGetMemberTaskEmail();

  const loadEmail = async () => {
    const response = await request({ emailRef, taskRef });
    if (response?.email) {
      setEmail(response.email);
    }
  };

  useEffect(() => {
    if (open && emailRef && taskRef) {
      loadEmail();
    }
  }, [open, emailRef, taskRef]);

  const handlePrint = () => {
    if (!email) return;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(email.html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.document.title = email.subject;
      setTimeout(
        () => {
          printWindow.print();
          printWindow.close();
        },
        1000 + email.attachments.length * 500,
      );
    }
    setMenuOpen(false);
  };

  const handleMenuToggle = () => {
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  };
  return (
    <>
      <ReactiveDialog
        open={open}
        onClose={onClose}
        fullWidthSize="lg"
        unpad={false}
      >
        {loading && <Loading />}
        {email && !loading && (
          <Box display="flex" flexDirection="column" gap="20px">
            {/* Email Subject */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="start"
              gap="15px"
            >
              <Typography
                variant="h3"
                sx={{
                  color: "#101828",
                  fontSize: "20px",
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                {email.subject}
              </Typography>
            </Box>

            <Divider />

            {/* Email Header */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="start"
              gap="15px"
            >
              <Box
                display="flex"
                flexDirection="row"
                gap="12px"
                alignItems="start"
                flex={1}
              >
                <Avatar sx={{ height: 40, width: 40, fontSize: "16px" }}>
                  {email.senderDisplayName[0]}
                </Avatar>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ width: "100%" }}
                >
                  <Typography variant="bodyHeavy" sx={{ opacity: ".8" }}>
                    {email.senderDisplayName}
                  </Typography>
                  <Typography variant="bodySmall" sx={{ color: "#616161" }}>
                    to {email.toAddresses.join(", ")}
                    {email.ccAddresses.length > 0 && (
                      <>, cc {email.ccAddresses.join(", ")}</>
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="end"
                gap="6px"
              >
                <DateDisplay
                  date={new Date(Number(email.timestampSec) * 1000)}
                  color="#475467"
                  sx={{
                    fontSize: "13px",
                    fontStyle: "italic",
                  }}
                />
                <Box>
                  <IconButton ref={anchorRef} onClick={handleMenuToggle}>
                    <EllipsisVertical size={20} color="#8E9598" />
                  </IconButton>
                  <PopperMenu
                    open={menuOpen}
                    anchorRef={anchorRef}
                    handleClose={handleMenuClose}
                  >
                    <MenuItem
                      sx={{
                        color: "#3D3D3D",
                        padding: "8px 16px",
                        minHeight: "inherit",
                      }}
                      onClick={handlePrint}
                    >
                      <Printer
                        height="16px"
                        width="16px"
                        style={{ marginRight: "8px" }}
                      />
                      Print
                    </MenuItem>
                  </PopperMenu>
                </Box>
              </Box>
            </Box>

            {/* Email Content */}
            <Box>
              <EmailLetter email={email} />
            </Box>
          </Box>
        )}
      </ReactiveDialog>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </>
  );
};
